import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  arrayToMap,
  canFromMap,
  emptyPermissionMap,
  MODULES,
  normalizePermissionMap,
} from "./permissionRegistry";
import { useUser } from "../UserContext";
import { getAuthToken, usesLocalApi } from "../services/deploymentConfig";
import { localPermissionService } from "../services/permissionLocalApiService";

const PermsCtx = createContext({ can: () => false, perms: null });

function buildFullPermMap() {
  const full = emptyPermissionMap();
  for (const [m, meta] of Object.entries(MODULES)) {
    full[m] = {};
    for (const act of meta.actions) full[m][act] = true;
  }
  return full;
}

function isAdminLike(user) {
  if (!user) return false;
  if (user.isOwner || user.isSuperAdmin) return true;
  const rn = (user.roleName || "").toString().trim().toLowerCase();
  const rid = (user.roleId || "").toString().trim().toLowerCase();
  // treat “admin” or “administrator” as elevated
  if (rn === "admin" || rn === "administrator") return true;
  if (rid === "admin" || rid === "administrator") return true;
  return false;
}

async function resolveRoleDocId(user, firestore) {
  const { query, where, collection, getDocs } = firestore;
  const { db } = await import("../firebase/firebase");
  if (typeof user?.roleId === "string" && user.roleId.trim()) return user.roleId.trim();

  if (user?.roleName) {
    const rolesRef = collection(db, "roles");

    if (user?.hospitalId) {
      const q1 = query(
        rolesRef,
        where("hospitalId", "==", user.hospitalId),
        where("name", "==", user.roleName)
      );
      const s1 = await getDocs(q1);
      if (!s1.empty) return s1.docs[0].id;
    }

    const q2 = query(
      rolesRef,
      where("isPredefined", "==", true),
      where("name", "==", user.roleName)
    );
    const s2 = await getDocs(q2);
    if (!s2.empty) return s2.docs[0].id;
  }

  return null;
}

export const PermissionsProvider = ({ children }) => {
  const { user } = useUser();
  const [permMap, setPermMap] = useState(null);

  useEffect(() => {
    let unsub = null;
    let cancelled = false;

    const init = async () => {
      if (!user) { setPermMap(null); return; }

      if (usesLocalApi()) {
        if (!getAuthToken()) {
          if (!cancelled) setPermMap(arrayToMap(user.permissions || []));
          return;
        }
        try {
          const data = await localPermissionService.getCurrentUserPermissions();
          if (!cancelled) setPermMap(data?.permissionsV2 || arrayToMap(data?.permissions || user.permissions || []));
        } catch {
          if (!cancelled) setPermMap(arrayToMap(user.permissions || []));
        }
        return;
      }

      const firestore = await import("firebase/firestore");
      const { onSnapshot, doc } = firestore;
      const { db } = await import("../firebase/firebase");

      // 👇 Elevate Admin exactly like Owner/SuperAdmin
      if (isAdminLike(user)) {
        if (!cancelled) setPermMap(buildFullPermMap());
        return;
      }

      const roleDocId = await resolveRoleDocId(user, firestore);

      if (roleDocId) {
        unsub = onSnapshot(doc(db, "roles", roleDocId), (snap) => {
          if (!snap.exists()) {
            setPermMap(arrayToMap(user.permissions || [])); // fallback to inline legacy
            return;
          }
          const data = snap.data();
          const map = data.permissionsV2
            ? normalizePermissionMap(data.permissionsV2)
            : arrayToMap(data.permissions || []);
          setPermMap(map);
        });
      } else {
        // No role doc—fallback to inline legacy array
        setPermMap(arrayToMap(user.permissions || []));
      }
    };

    init();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, [user]);

  const can = useMemo(
    () => (moduleKey, action = "view") =>
      isAdminLike(user) || canFromMap(permMap, moduleKey, action),
    [permMap, user]
  );

  return <PermsCtx.Provider value={{ can, perms: permMap }}>{children}</PermsCtx.Provider>;
};

export const useAccess = () => useContext(PermsCtx);
